import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Save,
  Cancel,
  ArrowBack,
  Folder,
  Email,
  Cloud,
  Api,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { partnerService, deliveryPreferenceService, DeliveryPreference } from '../services/api';
import { getErrorMessage } from '../utils/errorUtils';
import NotificationSnackbar, { NotificationType } from '../components/NotificationSnackbar';

/**
 * Admin screen to configure how a partner's settlement / mark-off files are delivered.
 * Four independent channels: folder mount, RabbitMQ queue, email, and REST pull.
 */
const DeliveryPreferences: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [partnerCode, setPartnerCode] = useState<string>('');
  const [partnerName, setPartnerName] = useState<string>('');
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: NotificationType;
  }>({ open: false, message: '', type: 'success' });

  const [form, setForm] = useState<Omit<DeliveryPreference, 'partnerCode' | 'partnerName'>>({
    folderEnabled: false,
    folderPath: '',
    queueEnabled: false,
    queueName: '',
    emailEnabled: false,
    emailRecipient: '',
    restEnabled: true,
  });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      // The delivery-preferences endpoint is keyed by partnerCode; resolve it from the partner id.
      const partner = await partnerService.getPartner(id);
      setPartnerCode(partner.partnerCode);
      setPartnerName(partner.partnerName);

      const prefs = await deliveryPreferenceService.getPreferences(partner.partnerCode);
      setForm({
        folderEnabled: !!prefs.folderEnabled,
        folderPath: prefs.folderPath || '',
        queueEnabled: !!prefs.queueEnabled,
        queueName: prefs.queueName || '',
        emailEnabled: !!prefs.emailEnabled,
        emailRecipient: prefs.emailRecipient || '',
        restEnabled: prefs.restEnabled !== false,
      });
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to load delivery preferences.');
      setNotification({ open: true, message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!partnerCode) return;

    const noChannel =
      !form.folderEnabled && !form.queueEnabled && !form.emailEnabled && !form.restEnabled;
    if (
      noChannel &&
      !window.confirm(
        'No delivery channel is enabled. This partner will not receive settlement files by any ' +
          'method, and REST pull will be blocked. Save anyway?'
      )
    ) {
      return;
    }

    setSaving(true);
    try {
      await deliveryPreferenceService.updatePreferences(partnerCode, {
        folderEnabled: form.folderEnabled,
        folderPath: form.folderPath || undefined,
        queueEnabled: form.queueEnabled,
        queueName: form.queueName || undefined,
        emailEnabled: form.emailEnabled,
        emailRecipient: form.emailRecipient || undefined,
        restEnabled: form.restEnabled,
      });
      setNotification({ open: true, message: 'Delivery preferences saved.', type: 'success' });
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to save delivery preferences.');
      setNotification({ open: true, message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate(`/partners/${id}`)}>
            Back
          </Button>
          <Box>
            <Typography variant="h4">Delivery Preferences</Typography>
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <Typography variant="body2" color="textSecondary">
                {partnerName} ({partnerCode})
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Typography variant="body2" color="textSecondary" mb={2}>
        Choose how settlement / mark-off files are delivered to this partner. Channels are independent
        — enable any combination.
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Folder */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                avatar={<Folder color={form.folderEnabled ? 'primary' : 'disabled'} />}
                title="Shared Folder"
                subheader="Copy the mark-off file into a partner-code-named folder on a shared mount."
              />
              <CardContent>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.folderEnabled}
                          onChange={(e) => setForm({ ...form, folderEnabled: e.target.checked })}
                        />
                      }
                      label="Enable folder delivery"
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="Folder path override (optional)"
                      value={form.folderPath}
                      onChange={(e) => setForm({ ...form, folderPath: e.target.value })}
                      disabled={!form.folderEnabled}
                      placeholder="Defaults to {base-path}/{partnerCode}"
                      helperText="Leave blank to use the default per-partner folder under the configured mount."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* RabbitMQ */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                avatar={<Cloud color={form.queueEnabled ? 'primary' : 'disabled'} />}
                title="RabbitMQ Queue"
                subheader="Publish the file to an auto-declared queue. Requires the broker to be enabled server-side."
              />
              <CardContent>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.queueEnabled}
                          onChange={(e) => setForm({ ...form, queueEnabled: e.target.checked })}
                        />
                      }
                      label="Enable queue delivery"
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="Queue name override (optional)"
                      value={form.queueName}
                      onChange={(e) => setForm({ ...form, queueName: e.target.value })}
                      disabled={!form.queueEnabled}
                      placeholder="Defaults to vps.settlement.{partnerCode}"
                      helperText="The queue is declared automatically when a settlement completes."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Email */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                avatar={<Email color={form.emailEnabled ? 'primary' : 'disabled'} />}
                title="Email"
                subheader="Email the mark-off file as an attachment."
              />
              <CardContent>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.emailEnabled}
                          onChange={(e) => setForm({ ...form, emailEnabled: e.target.checked })}
                        />
                      }
                      label="Enable email delivery"
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="Recipient override (optional)"
                      type="email"
                      value={form.emailRecipient}
                      onChange={(e) => setForm({ ...form, emailRecipient: e.target.value })}
                      disabled={!form.emailEnabled}
                      placeholder="Defaults to the partner's primary email"
                      helperText="Leave blank to use the partner's primary email address."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* REST pull */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                avatar={<Api color={form.restEnabled ? 'primary' : 'disabled'} />}
                title={
                  <Box display="flex" alignItems="center" gap={1}>
                    REST Pull
                    <Chip label="Preferred" color="primary" size="small" />
                  </Box>
                }
                subheader="The partner retrieves their settlement entries per day via the API using their API key."
              />
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.restEnabled}
                      onChange={(e) => setForm({ ...form, restEnabled: e.target.checked })}
                    />
                  }
                  label="Allow REST pull (GET /partner/settlements)"
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={() => navigate(`/partners/${id}`)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                  disabled={saving || !partnerCode}
                >
                  {saving ? 'Saving...' : 'Save Preferences'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </form>

      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ ...notification, open: false })}
      />
    </Box>
  );
};

export default DeliveryPreferences;
